/**
 * 听力挖空生成器
 * 根据用户整句听写的错误，动态生成挖空模板
 */

import {
  CardRecord,
  NewCardRecord,
  MyIELTSDatabase,
  defaultDb,
  createCard,
  mergeCard,
  ListeningMeta,
  ListeningContent,
} from '../db/indexedDb';
import { ListeningSentenceResult } from './types';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 48);

const placeholderForIndex = (index: number) => `{{blank_${index}}}`;

export interface GenerateListeningClozeResult {
  created: boolean;
  updated: boolean;
  cardId?: string;
  reason?: string;
}

/**
 * 从听写结果中提取错误词汇
 */
function extractWrongWords(result: ListeningSentenceResult): string[] {
  const wrongWords: string[] = [];
  
  // 从 diff 中提取 missing 和 substitution 的词汇
  for (const chunk of result.diff) {
    if ((chunk.kind === 'missing' || chunk.kind === 'substitution') && chunk.expected) {
      // 过滤掉太短的词和停用词
      const word = chunk.expected.trim();
      if (word.length >= 3 && !isStopWord(word)) {
        wrongWords.push(word);
      }
    }
  }
  
  // 也从 summary 中提取
  if (result.summary?.missing) {
    for (const word of result.summary.missing) {
      if (word.length >= 3 && !isStopWord(word) && wrongWords.indexOf(word) < 0) {
        wrongWords.push(word);
      }
    }
  }
  
  return wrongWords.slice(0, 5); // 最多 5 个挖空
}

/**
 * 停用词检查
 */
function isStopWord(word: string): boolean {
  const stopWords = [
    'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should',
    'may', 'might', 'must', 'can', 'to', 'of', 'in', 'on', 'at', 'for', 'with',
    'by', 'from', 'and', 'or', 'but', 'not', 'it', 'this', 'that', 'they', 'we',
    'he', 'she', 'i', 'you', 'my', 'your', 'his', 'her', 'its', 'our', 'their',
  ];
  return stopWords.indexOf(word.toLowerCase()) >= 0;
}

/**
 * 构建挖空模板
 */
function buildClozeTemplate(
  transcript: string,
  wrongWords: string[]
): { template: string; answers: string[]; indexes: number[] } | null {
  if (!transcript.trim() || wrongWords.length === 0) {
    return null;
  }

  let template = transcript;
  const answers: string[] = [];
  const indexes: number[] = [];

  for (const word of wrongWords) {
    // 使用词边界匹配，忽略大小写
    const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    const match = template.match(regex);
    
    if (match && match.index !== undefined) {
      const originalWord = match[0];
      template = template.replace(regex, placeholderForIndex(answers.length));
      answers.push(originalWord);
      indexes.push(match.index);
    }
  }

  if (answers.length === 0) {
    return null;
  }

  return { template, answers, indexes };
}

/**
 * 生成/更新听力挖空卡片
 * - 一源一卡：每个源卡片只保留一张挖空卡
 * - 基于用户错误动态生成
 */
export async function generateListeningClozeFromResult(
  sourceCard: CardRecord,
  result: ListeningSentenceResult,
  db: MyIELTSDatabase = defaultDb
): Promise<GenerateListeningClozeResult> {
  // 只处理准确率 < 95% 的结果
  if (result.accuracy >= 95) {
    return { created: false, updated: false, reason: 'accuracy-too-high' };
  }

  const wrongWords = extractWrongWords(result);
  if (wrongWords.length === 0) {
    return { created: false, updated: false, reason: 'no-wrong-words' };
  }

  const transcript = result.transcript || sourceCard.listeningMeta?.transcript || '';
  const cloze = buildClozeTemplate(transcript, wrongWords);
  if (!cloze) {
    return { created: false, updated: false, reason: 'cloze-not-built' };
  }

  const id = `${sourceCard.contentId ?? sourceCard.id}-listening-cloze`;
  const now = Date.now();

  const sourceMeta = sourceCard.listeningMeta ?? {};
  const sourceContent = (sourceCard.content ?? {}) as ListeningContent;

  const newCard: NewCardRecord = {
    id,
    type: 'listening',
    mode: 'review', // 挖空卡片是 review 模式
    isRelearn: false,
    topics: sourceCard.topics ?? [],
    tags: [...(sourceCard.tags ?? []), 'listening-cloze'],
    unitId: sourceCard.unitId,
    sourceId: sourceCard.id,
    contentId: id,
    contentVersion: `listening-cloze-${now}`,
    nextReviewDate: now, // 立即可练习
    content: {
      segment: sourceContent.segment,
      meta: sourceContent.meta, // 复制源卡片的 meta（包含 title）
      transcript,
      chineseText: (sourceContent as Record<string, unknown>).chineseText as string | undefined,
      clozeTemplate: cloze.template,
      clozeAnswers: cloze.answers,
      sourceCardId: sourceCard.id,
      generatedAt: now,
    },
    listeningMeta: {
      materialId: sourceMeta.materialId,
      audioPath: sourceMeta.audioPath,
      audioKey: sourceMeta.audioKey,
      start: sourceMeta.start,
      end: sourceMeta.end,
      transcript,
      clozeTemplate: cloze.template,
      clozeIndexes: cloze.indexes,
      clozeAnswers: cloze.answers,
      initialMode: 'review',
    },
  };

  const existing = await db.cards.get(id);
  if (!existing) {
    await createCard(newCard, db);
    return { created: true, updated: false, cardId: id };
  }

  // 更新现有卡片，保留 SRS 状态
  const merged = mergeCard(existing, newCard, now, true);
  merged.listeningMeta = {
    ...(merged.listeningMeta ?? {}),
    clozeTemplate: cloze.template,
    clozeIndexes: cloze.indexes,
    clozeAnswers: cloze.answers,
  };
  merged.content = { ...(existing.content ?? {}), ...newCard.content };
  merged.updatedAt = now;

  await db.cards.put(merged);
  return { created: false, updated: true, cardId: id };
}
